import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

export interface ConditionalDefaultSyncProps {
  // The form field the default is written to.
  name: string;
  // The field's configured default, already read back into what the control holds.
  defaultValue: unknown;
}

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/**
 * Puts a field's configured default in place the first time a display
 * condition lets the field through.
 *
 * A field hidden when the offer was created has no saved value, so revealing
 * it later left an empty box where the admin had set a default — the default
 * only ever reached fields that were visible at creation (CORE-5487). The
 * ConditionalFields renders this after the field's container, and a hidden
 * field renders nothing, so mounting is the moment it became visible. It has
 * to sit beside the container, not inside it: the container writes the value it
 * rendered with back on mount, and a child's effect runs first, so a seed from
 * in there was overwritten with the blank.
 *
 * Seeds once and only into a blank: a saved value belongs to the offer, and a
 * box the filler has cleared on purpose is their decision, not an empty one to
 * fill in again.
 */
export const ConditionalDefaultSync = ({
  name,
  defaultValue,
}: ConditionalDefaultSyncProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (isBlank(defaultValue)) return;
    if (!isBlank(values[name])) return;
    setFieldValue(name, defaultValue);
    // Runs on mount alone: mounting is the reveal, and re-running on every
    // value change would put the default back each time it is cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
